import type {
  CreateCheckoutSessionInput,
  SubscriptionRecurring,
} from '@affine/graphql';
import {
  cancelSubscriptionMutation,
  createCheckoutSessionMutation,
  getWorkspaceSubscriptionQuery,
  pricesQuery,
  resumeSubscriptionMutation,
  SubscriptionPlan,
  subscriptionQuery,
  updateSubscriptionMutation,
} from '@affine/graphql';
import { Store } from '@toeverything/infra';

import type { GlobalCache } from '../../storage';
import type { UrlService } from '../../url';
import type { SubscriptionType } from '../entities/subscription';
import type { GraphQLService } from '../services/graphql';
import type { ServerService } from '../services/server';
import { getCookie, getVisitorIdByCookie } from '../server/utils';
import { apiVersion, getCoreUrl, getApiUrl } from '../server/config';
import request from '../server/base';
import type {
  CreateAddonCheckoutSessionInput,
  AddonPrice,
  AddonPriceFromServer,
} from '../types';
const SUBSCRIPTION_CACHE_KEY = 'subscription:';
const ProductPackageMap = {
  pro: 1,
  studio: 2,
};

const getDefaultSubscriptionSuccessCallbackLink = (
  baseUrl: string,
  plan?: SubscriptionPlan | null,
  scheme?: string
) => {
  const path =
    plan === SubscriptionPlan.Team
      ? '/upgrade-success/team'
      : plan === SubscriptionPlan.AI
        ? '/ai-upgrade-success'
        : '/upgrade-success';
  const urlString = baseUrl + path;
  const url = new URL(urlString);
  if (scheme) {
    url.searchParams.set('scheme', scheme);
  }
  return url.toString();
};

export class SubscriptionStore extends Store {
  constructor(
    private readonly gqlService: GraphQLService,
    private readonly globalCache: GlobalCache,
    private readonly urlService: UrlService,
    private readonly serverService: ServerService
  ) {
    super();
  }

  async fetchSubscriptions(abortSignal?: AbortSignal) {
    const data = await this.gqlService.gql({
      query: subscriptionQuery,
      context: {
        signal: abortSignal,
      },
    });

    if (!data.currentUser) {
      throw new Error('No logged in');
    }

    return {
      userId: data.currentUser?.id,
      subscriptions: data.currentUser?.subscriptions,
    };
  }

  async fetchWorkspaceSubscriptions(
    workspaceId: string,
    abortSignal?: AbortSignal
  ) {
    const data = await this.gqlService.gql({
      query: getWorkspaceSubscriptionQuery,
      variables: {
        workspaceId,
      },
      context: {
        signal: abortSignal,
      },
    });

    if (!data.workspace) {
      throw new Error('No workspace');
    }

    return {
      workspaceId: data.workspace.subscription?.id,
      subscription: data.workspace.subscription,
    };
  }

  async mutateResumeSubscription(
    idempotencyKey: string,
    plan?: SubscriptionPlan,
    abortSignal?: AbortSignal,
    workspaceId?: string
  ) {
    const data = await this.gqlService.gql({
      query: resumeSubscriptionMutation,
      variables: {
        plan,
        workspaceId,
      },
      context: {
        signal: abortSignal,
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
      },
    });
    return data.resumeSubscription;
  }

  async mutateCancelSubscription(
    idempotencyKey: string,
    plan?: SubscriptionPlan,
    abortSignal?: AbortSignal,
    workspaceId?: string
  ) {
    const data = await this.gqlService.gql({
      query: cancelSubscriptionMutation,
      variables: {
        plan,
        workspaceId,
      },
      context: {
        signal: abortSignal,
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
      },
    });
    return data.cancelSubscription;
  }

  getCachedSubscriptions(userId: string) {
    return this.globalCache.get<SubscriptionType[]>(
      SUBSCRIPTION_CACHE_KEY + userId
    );
  }

  setCachedSubscriptions(userId: string, subscriptions: SubscriptionType[]) {
    return this.globalCache.set(SUBSCRIPTION_CACHE_KEY + userId, subscriptions);
  }

  getCachedWorkspaceSubscription(workspaceId: string) {
    return this.globalCache.get<SubscriptionType>(
      SUBSCRIPTION_CACHE_KEY + workspaceId
    );
  }

  setCachedWorkspaceSubscription(
    workspaceId: string,
    subscription: SubscriptionType
  ) {
    return this.globalCache.set(
      SUBSCRIPTION_CACHE_KEY + workspaceId,
      subscription
    );
  }

  setSubscriptionRecurring(
    idempotencyKey: string,
    recurring: SubscriptionRecurring,
    plan?: SubscriptionPlan,
    workspaceId?: string
  ) {
    return this.gqlService.gql({
      query: updateSubscriptionMutation,
      variables: {
        plan,
        recurring,
        workspaceId,
      },
      context: {
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
      },
    });
  }

  // async createCheckoutSession(input: CreateCheckoutSessionInput) {
  //   console.log('checkoutOptionsinput', input);
  //   const visitorId = getVisitorIdByCookie();
  //   let authToken = getCookie('login_token');
  //   const createorderConfig = {
  //     url: `${getApiUrl()}/trade/order/create-order`,
  //     method: 'POST',
  //     headers: {
  //       'X-Yuanshi-Authorization': 'Bearer ' + authToken,
  //       'X-Yuanshi-DeviceId': visitorId,
  //       // userId: auth?.user?.id,
  //     },
  //     data: {
  //       productId: input.productId,
  //       price: input.price.toString(),
  //     },
  //   };
  //   try {
  //     const res = await request(createorderConfig);
  //     console.log('aaaacreateorderRes', res);
  //     if (res.code === 0 && !res?.data?.errcode) {
  //       const businessOrderId = res.data;
  //       const checkoutSessionConfig = {
  //         url: `${getApiUrl()}/trade/payment/create-and-pay`,
  //         method: 'POST',
  //         headers: {
  //           'X-Yuanshi-Authorization': 'Bearer ' + authToken,
  //           'X-Yuanshi-DeviceId': visitorId,
  //           // userId: auth?.user?.id,
  //         },
  //         data: {
  //           businessOrderId,
  //           paymentMethod: '5', //todo,
  //           extData: {
  //             successCallbackLink: input.successCallbackLink,
  //           },
  //         },
  //       };
  //       const checkoutSessionRes = await request(checkoutSessionConfig);
  //       console.log('aaaacheckoutSessionRes', checkoutSessionRes);
  //       if (
  //         checkoutSessionRes.code === 0 &&
  //         !checkoutSessionRes?.data?.errcode
  //       ) {
  //         return JSON.parse(checkoutSessionRes.data?.payData || '{}')?.code_url;
  //       }
  //     }
  //   } catch (e) {
  //     console.log('aaaacreateorderFail', e);
  //   }
  // }
  async createCheckoutSession(input: CreateCheckoutSessionInput) {
    console.log('checkoutOptionsinput', input);
    const data = await this.gqlService.gql({
      query: createCheckoutSessionMutation,
      variables: {
        input: {
          ...input,
          successCallbackLink:
            input.successCallbackLink ||
            getDefaultSubscriptionSuccessCallbackLink(
              this.serverService.server.baseUrl,
              input.plan,
              this.urlService.getClientScheme()
            ),
        },
      },
    });
    return data.createCheckoutSession;
  }

  async getSubscribeModalData(locale: string) {
    let authToken = getCookie('login_token');
    const config = {
      url: `${getCoreUrl(apiVersion)}/activity/homepage`,
      method: 'GET',
      headers: {
        'X-Yuanshi-Locale': locale,
      },
    };
    if (authToken) {
      config.headers['X-Yuanshi-Authorization'] = 'Bearer ' + authToken;
    }
    return request(config);
  }

  async fetchSubscriptionPrices(abortSignal?: AbortSignal) {
    // const visitorId = getVisitorIdByCookie();
    // let authToken = getCookie('login_token');
    // const config = {
    //   url: `${getApiUrl()}/trade/product/list?productType=3&productNature=2&pricingCurrency=USD`,
    //   method: 'GET',

    //   headers: {
    //     'X-Yuanshi-Authorization': 'Bearer ' + authToken,
    //     'X-Yuanshi-DeviceId': visitorId,
    //     // userId: auth?.user?.id,
    //   },
    // };

    // try {
    //   const res = await request(config);
    //   console.log('aaaapriceurl', res);
    //   let prices = [] as Array<{
    //     plan: SubscriptionPlan;
    //     priciongCurrency: string;
    //     monthlyOriginalAmount: number;
    //     monthlySellingAmount: number;
    //     yearlyOriginalAmount: number;
    //     yearlySellingAmount: number;
    //     monthlyId: number;
    //     monthlyChannelProductId: string;
    //     yearlyId: number;
    //     yearlyChannelProductId: string;
    //     lifetimeAmount: number;
    //   }>;
    //   if (res.code === 0 && !res?.data?.errcode) {
    //     const priceList = res.data;
    //     const proPlan = {
    //       plan: SubscriptionPlan.Pro,
    //       priciongCurrency: '',
    //       monthlyOriginalAmount: 0,
    //       monthlySellingAmount: 0,
    //       yearlyOriginalAmount: 0,
    //       yearlySellingAmount: 0,
    //       monthlyId: 0,
    //       monthlyChannelProductId: '',
    //       yearlyId: 0,
    //       yearlyChannelProductId: '',
    //       lifetimeAmount: 0,
    //     };
    //     const studioPlan = {
    //       plan: SubscriptionPlan.Studio,
    //       priciongCurrency: '',
    //       monthlyOriginalAmount: 0,
    //       monthlySellingAmount: 0,
    //       yearlyOriginalAmount: 0,
    //       yearlySellingAmount: 0,
    //       monthlyId: 0,
    //       monthlyChannelProductId: '',
    //       yearlyId: 0,
    //       yearlyChannelProductId: '',
    //       lifetimeAmount: 0,
    //     };
    //     priceList.forEach(
    //       (item: {
    //         id: number;
    //         productType: number;
    //         productPackage: number;
    //         unit: string;
    //         totalQuantity: number;
    //         quantity: number;
    //         giftQuantity: number;
    //         pricingCurrency: string;
    //         originalPrice: number;
    //         sellingPrice: number;
    //         tag: number;
    //         channelProductId: string;
    //       }) => {
    //         console.log('aaaaapriceitem', item);
    //         if (
    //           item.productPackage === ProductPackageMap.pro &&
    //           item.unit === '月'
    //         ) {
    //           proPlan.monthlyOriginalAmount = item.originalPrice;
    //           proPlan.monthlySellingAmount = item.sellingPrice;
    //           proPlan.monthlyId = item.id;
    //           proPlan.monthlyChannelProductId = item.channelProductId;
    //         } else if (
    //           item.productPackage === ProductPackageMap.pro &&
    //           item.unit === '年'
    //         ) {
    //           proPlan.yearlyOriginalAmount = item.originalPrice;
    //           proPlan.yearlySellingAmount = item.sellingPrice;
    //           proPlan.yearlyId = item.id;
    //           proPlan.yearlyChannelProductId = item.channelProductId;
    //         } else if (
    //           item.productPackage === ProductPackageMap.studio &&
    //           item.unit === '月'
    //         ) {
    //           studioPlan.monthlyOriginalAmount = item.originalPrice;
    //           studioPlan.monthlySellingAmount = item.sellingPrice;
    //           studioPlan.monthlyId = item.id;
    //           studioPlan.monthlyChannelProductId = item.channelProductId;
    //         } else if (
    //           item.productPackage === ProductPackageMap.studio &&
    //           item.unit === '年'
    //         ) {
    //           studioPlan.yearlyOriginalAmount = item.originalPrice;
    //           studioPlan.yearlySellingAmount = item.sellingPrice;
    //           studioPlan.yearlyId = item.id;
    //           studioPlan.yearlyChannelProductId = item.channelProductId;
    //         }
    //       }
    //     );
    //     prices = [proPlan, studioPlan];
    //   }
    //   return prices;
    // } catch (e) {
    //   console.log(e);
    //   throw { status: 408 };
    // }
    const data = await this.gqlService.gql({
      query: pricesQuery,
      context: {
        signal: abortSignal,
      },
    });

    return data.prices;
  }
  async fetchAddonPrices() {
    const visitorId = getVisitorIdByCookie();
    let authToken = getCookie('login_token');
    const config = {
      url: `${getApiUrl()}/trade/product/list?productType=4&productNature=1&pricingCurrency=USD`,
      method: 'GET',

      headers: {
        'X-Yuanshi-Authorization': 'Bearer ' + authToken,
        'X-Yuanshi-DeviceId': visitorId,
        // userId: auth?.user?.id,
      },
    };

    try {
      const res = await request(config);

      let prices = [] as Array<AddonPrice>;
      if (res.code === 0 && !res?.data?.errcode) {
        const newPrices = res.data.map((item: AddonPriceFromServer) => ({
          ...item,
          originalPrice: (item.originalPrice / 100).toFixed(2),
          sellingPrice: (item.sellingPrice / 100).toFixed(2),
        }));
        console.log('aaaapriceurl', res.data, newPrices);
        prices = newPrices;
      }

      return prices;
    } catch (e) {
      console.log(e);
      throw { status: 408 };
    }
  }
  async createAddOnCheckoutSession(input: CreateAddonCheckoutSessionInput) {
    console.log('checkoutOptionsinput', input);
    const visitorId = getVisitorIdByCookie();
    let authToken = getCookie('login_token');
    const createorderConfig = {
      url: `${getApiUrl()}/trade/order/create-order`,
      method: 'POST',
      headers: {
        'X-Yuanshi-Authorization': 'Bearer ' + authToken,
        'X-Yuanshi-DeviceId': visitorId,
        // userId: auth?.user?.id,
      },
      data: {
        productId: input.productId,
        price: input.price.toString(),
      },
    };
    try {
      const res = await request(createorderConfig);
      console.log('aaaacreateorderRes', res);
      if (res.code === 0 && !res?.data?.errcode) {
        const businessOrderId = res.data;
        const checkoutSessionConfig = {
          url: `${getApiUrl()}/trade/payment/create-and-pay`,
          method: 'POST',
          headers: {
            'X-Yuanshi-Authorization': 'Bearer ' + authToken,
            'X-Yuanshi-DeviceId': visitorId,
            // userId: auth?.user?.id,
          },
          data: {
            businessOrderId,
            paymentMethod: '5', //todo,
            extData: {
              successCallbackLink: input.successCallbackLink,
            },
          },
        };
        const checkoutSessionRes = await request(checkoutSessionConfig);
        console.log('aaaacheckoutSessionRes', checkoutSessionRes);
        if (
          checkoutSessionRes.code === 0 &&
          checkoutSessionRes.data &&
          !checkoutSessionRes?.data?.errcode
        ) {
          return JSON.parse(checkoutSessionRes.data?.payData || '{}')?.code_url;
        } else {
          throw { msg: checkoutSessionRes.msg };
        }
      }
    } catch (e) {
      console.log('aaaacreateorderFail', e);
      throw { msg: e?.msg };
    }
  }
}
